import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Collapse,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  LocalShipping,
  Notifications,
  ExitToApp,
  People,
  Person,
  LocationOn,
  Today,
  DateRange,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  History,
  Textsms,
  ReportProblem,
  Assessment,
  Settings,
  CardMembership,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import OrderManagement from './OrderManagement';
import Analytics from './Analytics';
import Alerts from './Alerts';
import Overview from './Overview';
import DriverTracking from './DriverTracking';
import Drivers from './Drivers';
import TodaysCollections from './TodaysCollections';
import UpcomingCollections from './UpcomingCollections';
import PreviousCollections from './PreviousCollections';
import UserAccess from './UserAccess';
import SMSLog from './SMSLog';
import Reports from './Reports';
import CollectionDays from './CollectionDays';
import Loyalty from './Loyalty';
import api from '../services/api';

const drawerWidth = 240;

const Dashboard: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentView, setCurrentView] = useState('overview');
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [driverManagementOpen, setDriverManagementOpen] = useState(false);
  const [issueCount, setIssueCount] = useState(0);
  const { logout, user } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollectionsClick = () => {
    setCollectionsOpen(!collectionsOpen);
    // Close drivers menu when opening collections
    if (!collectionsOpen) {
      setDriverManagementOpen(false);
    }
  };

  const handleDriverManagementClick = () => {
    setDriverManagementOpen(!driverManagementOpen);
    // Close collections menu when opening drivers
    if (!driverManagementOpen) {
      setCollectionsOpen(false);
    }
  };

  const handleMenuClick = (view: string) => {
    setCurrentView(view);
    // Close all dropdowns when clicking a regular menu item
    setCollectionsOpen(false);
    setDriverManagementOpen(false);
    // Clear issue count when viewing alerts
    if (view === 'alerts') {
      setIssueCount(0);
    }
  };

  useEffect(() => {
    // Fetch issue count on mount and every 30 seconds
    const fetchIssueCount = async () => {
      try {
        const response = await api.get('/collection-issues/counts');
        const newCount = response.data.new_count || 0;
        setIssueCount(newCount);
      } catch (error) {
        console.error('Error fetching issue count:', error);
      }
    };

    fetchIssueCount();
    const interval = setInterval(fetchIssueCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const drawer = (
    <div>
      <Toolbar sx={{ padding: '10px' }}>
        <img 
          src="/logo.png" 
          alt="Bins & Bags" 
          style={{ 
            height: '40px', 
            width: 'auto',
            objectFit: 'contain'
          }} 
        />
      </Toolbar>
      <Divider />
      <List>
        {/* Overview */}
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'overview'}
            onClick={() => handleMenuClick('overview')}
          >
            <ListItemIcon><DashboardIcon /></ListItemIcon>
            <ListItemText primary="Overview" />
          </ListItemButton>
        </ListItem>

        {/* Collections with sub-items */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleCollectionsClick}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <ListItemIcon><DeleteIcon /></ListItemIcon>
            <ListItemText primary="Collections" />
            {collectionsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={collectionsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                sx={{ 
                  pl: 2,
                  backgroundColor: currentView === 'todays-collections' ? 'action.selected' : collectionsOpen ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentView === 'todays-collections' ? 'action.selected' : 'action.hover',
                  }
                }}
                selected={currentView === 'todays-collections'}
                onClick={() => setCurrentView('todays-collections')}
              >
                <ListItemIcon><Today /></ListItemIcon>
                <ListItemText 
                  primary="Today's Collections" 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{ 
                  pl: 2,
                  backgroundColor: currentView === 'upcoming-collections' ? 'action.selected' : collectionsOpen ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentView === 'upcoming-collections' ? 'action.selected' : 'action.hover',
                  }
                }}
                selected={currentView === 'upcoming-collections'}
                onClick={() => setCurrentView('upcoming-collections')}
              >
                <ListItemIcon><DateRange /></ListItemIcon>
                <ListItemText 
                  primary="Upcoming Collections" 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 2,
                  backgroundColor: currentView === 'previous-collections' ? 'action.selected' : collectionsOpen ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentView === 'previous-collections' ? 'action.selected' : 'action.hover',
                  }
                }}
                selected={currentView === 'previous-collections'}
                onClick={() => setCurrentView('previous-collections')}
              >
                <ListItemIcon><History /></ListItemIcon>
                <ListItemText
                  primary="Previous Collections"
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* Driver Management with sub-items */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleDriverManagementClick}
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <ListItemIcon><People /></ListItemIcon>
            <ListItemText primary="Drivers" />
            {driverManagementOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={driverManagementOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                sx={{ 
                  pl: 2,
                  backgroundColor: currentView === 'driver-list' ? 'action.selected' : driverManagementOpen ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentView === 'driver-list' ? 'action.selected' : 'action.hover',
                  }
                }}
                selected={currentView === 'driver-list'}
                onClick={() => setCurrentView('driver-list')}
              >
                <ListItemIcon><Person /></ListItemIcon>
                <ListItemText 
                  primary="Active Drivers" 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                sx={{ 
                  pl: 2,
                  backgroundColor: currentView === 'driver-tracking' ? 'action.selected' : driverManagementOpen ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentView === 'driver-tracking' ? 'action.selected' : 'action.hover',
                  }
                }}
                selected={currentView === 'driver-tracking'}
                onClick={() => setCurrentView('driver-tracking')}
              >
                <ListItemIcon><LocationOn /></ListItemIcon>
                <ListItemText 
                  primary="Driver Tracking" 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>

        {/* Other menu items */}
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'orders'}
            onClick={() => handleMenuClick('orders')}
          >
            <ListItemIcon><LocalShipping /></ListItemIcon>
            <ListItemText primary="Order Management" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'alerts'}
            onClick={() => handleMenuClick('alerts')}
          >
            <ListItemIcon>
              <Badge badgeContent={issueCount} color="error">
                <Notifications />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Issue Reports" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'sms-log'}
            onClick={() => handleMenuClick('sms-log')}
          >
            <ListItemIcon><Textsms /></ListItemIcon>
            <ListItemText primary="SMS Log" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'reports'}
            onClick={() => handleMenuClick('reports')}
          >
            <ListItemIcon><Assessment /></ListItemIcon>
            <ListItemText primary="Analytics Reports" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'loyalty'}
            onClick={() => handleMenuClick('loyalty')}
          >
            <ListItemIcon><CardMembership /></ListItemIcon>
            <ListItemText primary="Loyalty Scheme" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentView === 'collection-days'}
            onClick={() => handleMenuClick('collection-days')}
          >
            <ListItemIcon><Settings /></ListItemIcon>
            <ListItemText primary="Checkout Settings" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        {user?.role === 'admin' && (
          <ListItem disablePadding>
            <ListItemButton
              selected={currentView === 'user-access'}
              onClick={() => handleMenuClick('user-access')}
            >
              <ListItemIcon><People /></ListItemIcon>
              <ListItemText primary="User Access" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton onClick={logout}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'overview':
        return <Overview />;
      case 'todays-collections':
        return <TodaysCollections />;
      case 'upcoming-collections':
        return <UpcomingCollections />;
      case 'previous-collections':
        return <PreviousCollections />;
      case 'orders':
        return <OrderManagement />;
      case 'driver-list':
        return <Drivers />;
      case 'driver-tracking':
        return <DriverTracking />;
      case 'analytics':
        return <Analytics />;
      case 'alerts':
        return <Alerts />;
      case 'sms-log':
        return <SMSLog />;
      case 'reports':
        return <Reports />;
      case 'user-access':
        return <UserAccess />;
      case 'collection-days':
        return <CollectionDays />;
      case 'loyalty':
        return <Loyalty />;
      default:
        return <Overview />;
    }
  };

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#23a244',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Bins and Bags Dashboard
          </Typography>
          <Typography variant="body2">
            Welcome, {user?.name || 'Admin'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Container maxWidth={false}>
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;